import React, { useState } from 'react';
import {
  Box, VStack, Heading, Text, Button, ButtonText, ScrollView, Spinner,
  Alert, Icon, RadioGroup, Radio, RadioIndicator, RadioIcon, RadioLabel, CircleIcon, Textarea, TextareaInput, HStack,
  Checkbox, CheckboxGroup, CheckboxIndicator, CheckboxIcon, CheckboxLabel, CheckIcon,
} from '@gluestack-ui/themed';
import { AlertCircle } from 'lucide-react-native';
import { useProcessStage2ContentStudioStage2Post } from '@/common/api/endpoints/content-studio/content-studio';
import { ConceptGraphInput, QuestionAnswer } from '@/common/api/models';
import { useContentStudioStore } from './store';
import { LoadingOverlay } from './LoadingOverlay';

export const Stage3ScopingQuestions = () => {
  const {
    stage1Output, stage1Input, setStage2Output, setStage, stage2Input, setStage2Input, config,
  } = useContentStudioStore();
  const [answers, setAnswers] = useState<QuestionAnswer[]>(stage2Input.answers || []);

  const { mutate, isPending, isError } = useProcessStage2ContentStudioStage2Post({
    mutation: {
      onSuccess: (response) => {
        setStage2Output(response.data);
        setStage(4);
      },
    },
  });

  if (!stage1Output) return null;

  const handleSubmit = () => {
    const graphInput: ConceptGraphInput = {
      nodes: stage1Output.graph.nodes,
      edges: stage1Output.graph.edges,
      clusters: stage1Output.graph.clusters,
    };

    const payload = {
      selectedNodeIds: stage2Input.selectedNodeIds || [],
      answers,
      originalPrompt: stage1Input.prompt || '',
      graph: graphInput,
      config: {
        levels: config.moduleDepth,
        totalTasks: config.totalVolume,
        difficulty: config.difficulty.toLowerCase(),
        studyProgram: config.studyProgram,
      },
    };

    setStage2Input(payload);
    mutate({ data: payload });
  };

  const handleAnswer = (questionId: string, value: string | string[], type: 'text' | 'option') => {
    setAnswers((prev) => {
      const existing = prev.filter((a) => a.questionId !== questionId);
      const newAnswer: QuestionAnswer = { questionId };
      if (type === 'text') newAnswer.textResponse = value as string;
      if (type === 'option') {
        newAnswer.selectedOptionIds = Array.isArray(value) ? value : [value];
      }
      return [...existing, newAnswer];
    });
  };

  return (
    <Box flex={1} p="$6" maxWidth={800} alignSelf="center" w="100%">
      <LoadingOverlay isLoading={isPending} title="Generating Strategy" />
      <ScrollView>
        <VStack space="xl" pb="$10">
          <Box>
            <Heading size="xl">Scope Refinement</Heading>
            <Text color="$textSubtle" mt="$2">
              Help us understand the depth and focus for your selected concepts.
            </Text>
          </Box>

          <Box p="$4" bg="$bgSubtle" borderRadius="$md" borderWidth={1} borderColor="$borderLight">
            <Text size="sm" fontWeight="bold">Selection Summary</Text>
            <Text size="xs" color="$textSubtle" mt="$1">
              {stage2Input.selectedNodeIds?.length || 0}
              {' '}
              concepts selected for this module.
            </Text>
          </Box>

          {/* Scoping Questions */}
          {stage1Output.scopingQuestions && stage1Output.scopingQuestions.length > 0 && (
            <VStack space="lg" mt="$4">
              {stage1Output.scopingQuestions
                .filter((q) => !['config_levels', 'config_total_tasks', 'config_difficulty', 'config_study_program'].includes(q.id))
                .map((q) => (
                  <Box key={q.id} p="$4" bg="$white" borderRadius="$md" borderWidth={1} borderColor="$borderLight">
                    <Text fontWeight="bold" mb="$2">{q.text}</Text>
                    {q.helpText && <Text size="xs" color="$textSubtle" mb="$2">{q.helpText}</Text>}

                    {q.type === 'single_choice' && q.options && (
                      <RadioGroup
                        value={answers.find((a) => a.questionId === q.id)?.selectedOptionIds?.[0]}
                        onChange={(val) => handleAnswer(q.id, val, 'option')}
                      >
                        <VStack space="sm">
                          {q.options.map((opt) => (
                            <Radio key={opt.id} value={opt.id} size="sm">
                              <RadioIndicator mr="$2"><RadioIcon as={CircleIcon} /></RadioIndicator>
                              <RadioLabel>{opt.label}</RadioLabel>
                            </Radio>
                          ))}
                        </VStack>
                      </RadioGroup>
                    )}

                    {q.type === 'multiple_choice' && q.options && (
                      <CheckboxGroup
                        value={answers.find((a) => a.questionId === q.id)?.selectedOptionIds || []}
                        onChange={(val) => handleAnswer(q.id, val, 'option')}
                      >
                        <VStack space="sm">
                          {q.options.map((opt) => (
                            <Checkbox key={opt.id} value={opt.id} size="sm">
                              <CheckboxIndicator mr="$2"><CheckboxIcon as={CheckIcon} /></CheckboxIndicator>
                              <CheckboxLabel>{opt.label}</CheckboxLabel>
                            </Checkbox>
                          ))}
                        </VStack>
                      </CheckboxGroup>
                    )}

                    {q.type === 'text' && (
                      <Textarea>
                        <TextareaInput
                          placeholder="Type your answer..."
                          onChangeText={(val) => handleAnswer(q.id, val, 'text')}
                        />
                      </Textarea>
                    )}
                  </Box>
                ))}
            </VStack>
          )}

          {isError && (
            <Alert action="error" variant="solid" borderRadius="$md">
              <Icon as={AlertCircle} mr="$3" color="$textOnError" />
              <Text color="$textOnError">Failed to process request. Please try again.</Text>
            </Alert>
          )}

          <HStack space="md" mt="$4">
            <Button
              onPress={() => setStage(2)}
              variant="outline"
              flex={1}
            >
              <ButtonText>Back to Graph</ButtonText>
            </Button>
            <Button
              onPress={handleSubmit}
              isDisabled={isPending}
              flex={2}
            >
              {isPending && <Spinner color="$white" mr="$2" />}
              <ButtonText>{isPending ? 'Generating Strategy...' : 'Continue to Strategy'}</ButtonText>
            </Button>
          </HStack>
        </VStack>
      </ScrollView>
    </Box>
  );
};
